import { type FC, useMemo, useState } from 'react';
import {
  AllowedNegotiationActions,
  AllowedNegotiationModalActions,
  Negotiation,
} from '@interfaces/models/negotiation/negotiation';
import { NegotiationEvent } from '@interfaces/models/negotiation/negotiationEvent';
import { useNegotiation } from '@context/negotiation.context';
import { usePreferences } from '@context/preferences.context';
import { NegotiationsUtils } from '@services/negotiations-utils';
import UserService from '@services/user-service';
import { ImagesService } from '@services/image-service';
import useUser from '@hooks/user/use-user';
import NegotiationHistory from '@components/organisms/negotiation/negotiation-modal/history/negotiation-history';
import NegotiationFlashPriceDrop from '@components/organisms/negotiation/negotiation-modal/fpd/negotiation-fpd';
import { NegotiationModalProductInfo } from '@components/organisms/negotiation/negotiation-modal/product-info/negotiation-modal-product';
import NegotiationForm from '@components/organisms/negotiation/negotiation-modal/form/negotiation-form';
import { NegotiationModalOngoingOffers } from '../ongoing-offers/negotiation-modal-ongoing-offers';
import { useNegotiationModalPresenter } from '../negotiation-modal-presenter';
import styles from '../negotiation-modal.module.scss';
import { NegotiationModalPricingInsight } from '../pricing-insight/negotiation-modal-pricing-insight';
import { StatusComponent } from './status/status-component';

interface NegotiationModalInProgressNegotiationProps {
  trackingFrom: string;
  isShippingProhibited: boolean;
  onClose?: () => void;
  showFpdSection: boolean;
}

const CHAT_LINK_ROOT = '/chat?id=';

export const NegotiationModalInprogress: FC<NegotiationModalInProgressNegotiationProps> = ({
  isShippingProhibited,
  onClose,
  trackingFrom,
  showFpdSection,
}) => {
  const {
    negotiation,
    negotiationProduct,
    isNegotiationBannerVisible,
    isBuyNowCTAVisible,
    concurrentActiveNegotiations,
    setIsLoading,
  } = useNegotiationModalPresenter();
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useUser();
  const { setNegotiation } = useNegotiation();
  const { language } = usePreferences();
  const [currentAction, setCurrentAction] = useState<AllowedNegotiationModalActions>(null);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [isNextActionsShown, setIsNextActionsShown] = useState<boolean>(true);
  const maxOffer = Math.round(negotiation.maxOffer.cents / 100);
  const minOffer = Math.round(negotiation.minOffer.cents / 100);
  const pricingBreakdown = negotiationProduct.pricingBreakdown;
  const userRole = UserService.getUserNegotiationRole(user, negotiationProduct, negotiation);
  const isBuyer = userRole === NegotiationEvent.CreatedByEnum.Buyer;
  const isSeller = userRole === NegotiationEvent.CreatedByEnum.Seller;
  const nextActions: AllowedNegotiationActions[] = negotiation.nextActions || [];
  const initialPrice = negotiation.initialPrice;
  const isFpdDisplayed =
    negotiation.flashPriceDrop &&
    (negotiation.flashPriceDrop.isEligible ||
      (!negotiation.flashPriceDrop.isEligible && negotiation.flashPriceDrop.isActive));
  const isLastOffer = useMemo<boolean>(() => {
    if (!nextActions.includes('offer')) {
      return false;
    }
    if (userRole === NegotiationEvent.CreatedByEnum.Buyer) {
      return negotiation.buyerRemainingOffers === 1;
    }
    if (userRole === NegotiationEvent.CreatedByEnum.Seller) {
      return negotiation.sellerRemainingOffers === 1;
    }
    return false;
  }, [negotiation, userRole]);

  const selectAction = (action: AllowedNegotiationModalActions): void => {
    setCurrentAction(action);
  };

  const clearAction = (): void => {
    setCurrentAction(null);
  };

  const addOffer = async (price: number): Promise<void> => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const latestNegotiation: Negotiation = await NegotiationsUtils.sendOffer(negotiation, price);
      setCurrentAction(null);
      setLoading(false);
      setNegotiation(latestNegotiation);
    } catch (e) {
      setLoading(false);
      const error = e?.response?.data?.errors?.detail || null;
      if (error) {
        setErrorMessage(error);
      }
    }
  };

  const onSubmitNegotiationForm = (price: number) => {
    addOffer(price);
  };

  const onCancelNegotiationForm = (): void => {
    clearAction();
    setIsNextActionsShown(true);
  };

  const shouldShowWarning = (): boolean => {
    return isSeller && nextActions.length !== 0 && negotiation.product?.displayPricingInsight;
  };

  const showHistory = !!negotiation.buyer?.id && !!negotiation.product?.id;

  return (
    <>
      <div className={styles.mmao__innerWrapper}>
        {errorMessage && <p className={styles.mmao__error}>{errorMessage}</p>}
        <NegotiationModalProductInfo
          price={isSeller && !!pricingBreakdown ? pricingBreakdown?.sellerPrice?.formatted : initialPrice?.formatted}
          isSeller={isSeller}
          breakdown={negotiation.pricingBreakdown}
          brandName={negotiationProduct.brand.name}
          description={negotiationProduct.name}
          picturePath={ImagesService.buildUrl(`/produit/${negotiationProduct.pictures[0].path}`, { w: 244 }, true)}
          productPath={negotiation.product.path}
          onClose={onClose}
          alt={negotiationProduct.brand.name}
        />
        <NegotiationModalPricingInsight shouldShow={shouldShowWarning()} />
        {isNegotiationBannerVisible && (
          <NegotiationModalOngoingOffers
            trackingFrom={trackingFrom}
            showBuyNow={isBuyNowCTAVisible}
            setIsLoading={setIsLoading}
            onClose={onClose}
            isNegotiationAccepted={negotiation?.isAccepted}
            product={negotiationProduct}
            concurrentActiveNegotiations={concurrentActiveNegotiations}
          />
        )}
        <StatusComponent
          nextActions={nextActions}
          negotiation={negotiation}
          isLastOffer={isLastOffer}
          negotiationProduct={negotiationProduct}
          onClose={onClose}
          setErrorMessage={setErrorMessage}
          isSeller={isSeller}
          selectAction={selectAction}
          setLoading={setLoading}
          isLoading={loading}
          isNextActionsShown={isNextActionsShown}
          setIsNextActionsShown={setIsNextActionsShown}
        />
        <NegotiationForm
          isDisabled={isShippingProhibited}
          action={currentAction}
          currency={negotiation.price.currency}
          language={language}
          min={minOffer}
          max={maxOffer}
          onSubmit={onSubmitNegotiationForm}
          onCancel={onCancelNegotiationForm}
          productId={negotiationProduct.id}
          isSeller={isSeller}
        />
        {!isBuyer && isFpdDisplayed && (
          <NegotiationFlashPriceDrop
            showFpdSection={showFpdSection}
            trackingFrom={trackingFrom}
          />
        )}
      </div>
      {showHistory && (
        <NegotiationHistory
          chatLink={`${CHAT_LINK_ROOT}${negotiation.buyer.id}-${negotiation.product.id}`}
          events={negotiation.events}
          seller={negotiation.seller}
          buyer={negotiation.buyer}
          isBuyer={isBuyer}
        />
      )}
    </>
  );
};
